import '@/css/classes/symbol_picker.scss';

import {
    breakCamelCase,
    genId,
    highlightOnFirstClick,
    invertDict,
    isBlankValue,
    stripCharsFromEnd,
    toTitleCase,
} from '@/js/util.js';

import {
    assert,
    assertArray,
    assertIn,
    assertNonemptyObject,
    assertObject,
} from '@/js/util/asserts.js';

import {
    AttributeType,
    ASSET_TYPES_IN_PROJECT,
    ASSET_NAME,
    HexColor,
    MapSymbol,
    ASSET_TYPE_FEATURE_TYPE,
    FeatureType,
    CUSTOM_ATTRIB_NAME_MAX_LEN,
    CUSTOM_ATTRIB_VALUE_MAX_LEN,
    DEFAULT_COLOR_FIELD_VALUES,
} from '@/js/util/shared_constants.js';

import { getCustomAttribName } from '@/js/util/shared_functionality.js';
import { ErrorModal } from '@/js/classes/modal.js';
import { CollapsingDiv } from '@/js/classes/collapsingdiv.js';
import { SymbolPicker } from '@/js/classes/symbol_picker.js';
import { getSymbolSvg } from '@/js/util/map_functionality.js';

import { cloneDeep } from 'lodash-es';


export class ProjectCustomizeForm {
    static attribute_types_numeric = [
        AttributeType.Number,
        AttributeType.Integer,
        AttributeType.Dbh,
    ];

    static attribute_types_with_option_colors = [
        AttributeType.SingleOption,
        AttributeType.DropdownOption,
        AttributeType.ButtonSingleOption,
    ];

    static DATATYPE_NICKNAMES = {
        'Single line Text Box': AttributeType.String,
        'Multi-line Text Box': AttributeType.BigString,
        'Number with Decimals': AttributeType.Number,
        'Whole Number, no Decimals': AttributeType.Integer,
        'Single-select List': AttributeType.SingleOption,
        'Multi-select List': AttributeType.MultiOption,
    };

    static DATATYPE_NICKNAMES_LOOKUP = invertDict(ProjectCustomizeForm.DATATYPE_NICKNAMES);


    constructor(opts) {
        assert('form_elem' in opts);
        assert('project_type' in opts);
        assert('used_attribute_internal_names' in opts);
        assert('color_field_values' in opts);
        assert('custom_attributes' in opts);

        assertObject(opts['used_attribute_internal_names']);
        assertObject(opts['color_field_values']);
        assertObject(opts['custom_attributes']);

        this.form_elem = opts['form_elem']; //node
        this.project_type = opts['project_type']; //node
        this.used_attribute_internal_names = opts['used_attribute_internal_names']; //nested dictionary
        this.color_field_values = opts['color_field_values']; //dictionary
        this.custom_attributes = opts['custom_attributes']; //nested dictionary
        this.can_delete_custom_attribs = opts['can_delete_custom_attribs'] ?? false; //boolean

        this.form_id = 'projectform_'+genId();
        this.asset_types = ASSET_TYPES_IN_PROJECT[this.project_type] || [];
        this.asset_data = {};
        this.multiple_asset_types = (this.asset_types.length > 1);

        assertIn('collector_attributes', window.newcollector);


        const already_generated_color_fields = new Set();
        const repeated_color_fields = new Set();
        const color_fields_by_asset_type = {};
        const repeated_color_field_feature_types = {};
        for(const asset_type of this.asset_types) {
            const color_fields = new Set(ProjectCustomizeForm.getColorFields(asset_type));
            color_fields_by_asset_type[asset_type] = color_fields;
            for(const color_field of color_fields) {
                if(already_generated_color_fields.has(color_field)) {
                    repeated_color_fields.add(color_field);
                    const feature_type = ASSET_TYPE_FEATURE_TYPE[asset_type] ?? FeatureType.Point;
                    if(color_field in repeated_color_field_feature_types) {
                        repeated_color_field_feature_types[color_field].add(feature_type);
                    } else {
                        repeated_color_field_feature_types[color_field] = new Set([feature_type]);
                    }
                }
                already_generated_color_fields.add(color_field);
            }
        }
        for(const asset_type of this.asset_types) {
            color_fields_by_asset_type[asset_type] = color_fields_by_asset_type[asset_type].difference(repeated_color_fields);
        }
        this.repeated_color_fields = repeated_color_fields;
        this.repeated_color_fields_feature_type = {};
        for(const color_field of repeated_color_fields) {
            const feature_types = [...repeated_color_field_feature_types[color_field]];
            const has_point = feature_types.some(feature_type => feature_type===FeatureType.Point);
            const has_non_point = feature_types.some(feature_type => feature_type!==FeatureType.Point);
            if(has_point && has_non_point) {
                this.repeated_color_fields_feature_type[color_field] = null;
            } else {
                this.repeated_color_fields_feature_type[color_field] = feature_types[0];
            }
        }


        for(const asset_type of this.asset_types) {
            assertIn(asset_type, window.newcollector.collector_attributes);
            assertArray(window.newcollector.collector_attributes[asset_type]);

            const used_names = this.used_attribute_internal_names[asset_type] || [];
            const custom_attrs = this.custom_attributes[asset_type] || [];

            used_names.sort();
            const does_have_custom_attribs = custom_attrs.length > 0;

            let attributes = cloneDeep(window.newcollector.collector_attributes[asset_type]);
            if(does_have_custom_attribs) { attributes = attributes.concat(custom_attrs); }

            for(let idx = 0; idx < attributes.length; idx++) {
                attributes[idx].used = used_names.includes(attributes[idx].internal_name);
            }

            const attributes_by_category = attributes.reduce((accumulator, attribute) => {
                const category = attribute['category_name'];
                if(!accumulator[category]) { accumulator[category] = []; }
                accumulator[category].push(attribute);
                return accumulator;
            }, {});

            this.asset_data[asset_type] = {
                attributes: attributes,
                attributes_by_category: attributes_by_category,
                color_fields: [...color_fields_by_asset_type[asset_type]],
                input_group_elems: {},
                does_have_custom_attribs: does_have_custom_attribs,
                asset_type_container: null
            };
        }
    }

    static getColorFields(asset_type) {
        const attributes = window.newcollector.collector_attributes[asset_type];
        const color_fields = new Set(['default:'+ASSET_NAME[asset_type]]);
        for(const attribute of attributes) {
            if(!attribute['hasColors']) { continue; }
            const attribute_type = attribute['attribute_type'];
            if(ProjectCustomizeForm.attribute_types_with_option_colors.includes(attribute_type)) {
                for(const option of attribute['options']) {
                    color_fields.add(attribute['internal_name']+':'+option);
                }

            } else if(ProjectCustomizeForm.attribute_types_numeric.includes(attribute_type)) {
                color_fields.add(attribute['internal_name']+':minimum');
                color_fields.add(attribute['internal_name']+':maximum');

            } else {
                console.error('hasColors not supported for attribute of type '+attribute_type);
            }
        }
        return [...color_fields];
    }

    getMapSymbolSvgs(feature_type) {
        if(this._map_symbol_svgs) {
            if(feature_type===null) { return this._map_symbol_svgs['both']; }
            if(feature_type===FeatureType.Point) { return this._map_symbol_svgs['point']; }
            return this._map_symbol_svgs['line'];
        }

        this._map_symbol_svgs = {
            'both': {},
            'point': {},
            'line': {},
        };

        for(const symbol of Object.values(MapSymbol)) {
            const svg = getSymbolSvg(symbol, 12, HexColor.Black, HexColor.Black, 0, false);
            this._map_symbol_svgs['both'][symbol] = svg
            if(symbol.endsWith('_line')) {
                this._map_symbol_svgs['line'][symbol] = svg;
            } else {
                this._map_symbol_svgs['point'][symbol] = svg;
            }
        }

        if(feature_type===null) { return this._map_symbol_svgs['both']; }
        if(feature_type===FeatureType.Point) { return this._map_symbol_svgs['point']; }
        return this._map_symbol_svgs['line'];
    }

    _genCustomAttribute(asset_type, attribute) {
        assertNonemptyObject(attribute);

        const attribute_type = attribute.attribute_type;
        const visible_name = getCustomAttribName(attribute.name);
        const visible_type = ProjectCustomizeForm.DATATYPE_NICKNAMES_LOOKUP[attribute_type];
        const row_id = this.form_id+'_'+asset_type+'_'+attribute.internal_name;
        const using_id = this.form_id+'_'+asset_type+'_'+attribute.internal_name+'_using';
        const can_be_deleted = this.can_delete_custom_attribs || ('new' in attribute);

        const container_elem = document.createElement('div');
        container_elem.className = 'custom-attribute';
        container_elem.id = row_id;

        /* Container heading */ {
            const container_heading_wrapper = document.createElement('div');
            container_heading_wrapper.classList.add('d-flex', 'justify-content-between');
            container_elem.appendChild(container_heading_wrapper);

            const container_heading = document.createElement('div');
            container_heading_wrapper.appendChild(container_heading);

            const checkbox_elem = document.createElement('input');
            checkbox_elem.type = 'checkbox';
            checkbox_elem.className = 'custom-attribute-using-checkbox';
            checkbox_elem.id = using_id;
            if(attribute.used === true) { checkbox_elem.checked = true; }
            container_heading.appendChild(checkbox_elem);

            const checkbox_label_elem = document.createElement('label');
            checkbox_label_elem.className = 'custom-attribute-heading';
            checkbox_label_elem.htmlFor = using_id;
            checkbox_label_elem.textContent = `Custom ${visible_type} Attribute`;
            container_heading.appendChild(checkbox_label_elem);

            const attrib_delete_button = document.createElement('button');
            attrib_delete_button.classList.add('custom-attrib-delete-btn');
            if(!can_be_deleted) { attrib_delete_button.classList.add('custom-attrib-delete-btn-disabled'); }
            attrib_delete_button.type = 'button';
            attrib_delete_button.innerText = 'X';
            container_heading_wrapper.appendChild(attrib_delete_button);

            attrib_delete_button.addEventListener('click', () => {
                if(!can_be_deleted) {
                    new ErrorModal('<h5>Since this project already has assets, custom attributes cannot be deleted.</h5>').show();
                    return;
                }

                this.asset_data[asset_type].attributes = this.asset_data[asset_type].attributes.filter(
                    attrib => attrib.internal_name !== attribute.internal_name
                );
                container_elem.remove();
            });
        }

        const name_elem = document.createElement('div');
        name_elem.className = 'custom-attribute-name';
        container_elem.appendChild(name_elem);

        const name_label_elem = document.createElement('label');
        name_label_elem.textContent = 'Name:';
        name_elem.appendChild(name_label_elem);

        const name_input_elem = document.createElement('input');
        name_input_elem.type = 'text';
        name_input_elem.value = visible_name;
        name_elem.appendChild(name_input_elem);
        highlightOnFirstClick(name_input_elem);

        const value_elem = document.createElement('div');
        value_elem.className = 'custom-attribute-value';
        container_elem.appendChild(value_elem);

        if(([AttributeType.SingleOption, AttributeType.MultiOption]).includes(attribute_type)) {
            const textarea = document.createElement('textarea');
            textarea.placeholder = 'Options, one per line';
            textarea.textContent = attribute.options.join('\n');
            value_elem.appendChild(textarea);
        }

        return container_elem;
    }

    _addAttributeCategory(asset_type, category_name, attributes) {
        const input_group_elem = document.createElement('div');
        input_group_elem.classList.add('input-row');
        input_group_elem.id = this.form_id+'_'+asset_type+'_'+category_name;
        const label_text = toTitleCase(category_name);

        if(category_name === 'custom') {
            input_group_elem.appendChild(document.createElement('hr'));

            const heading_elem = document.createElement('div');
            heading_elem.classList.add('category-heading');
            input_group_elem.appendChild(heading_elem);

            const label_elem = document.createElement('label');
            label_elem.innerText = 'Add your own Custom Fields:';
            heading_elem.appendChild(label_elem);

        } else {
            const all_attributes_are_required = (attributes.filter((attrib) => !attrib.required).length === 0)
            const only_has_one_attribute = (attributes.length === 1);

            const heading_elem = document.createElement('div');
            heading_elem.classList.add('category-heading');
            input_group_elem.appendChild(heading_elem);

            const label_elem = document.createElement('label');
            label_elem.innerText = label_text;
            heading_elem.appendChild(label_elem);

            const wrapper_elem = document.createElement('div');
            wrapper_elem.classList.add('d-flex', 'gap-1');
            heading_elem.appendChild(wrapper_elem);

            if(!all_attributes_are_required && !only_has_one_attribute) {
                const select_button_elem = document.createElement('button');
                select_button_elem.classList.add('project-customize-select-button');
                select_button_elem.type = 'button';
                select_button_elem.innerText = 'Select All';
                select_button_elem.setAttribute('data-action', 'select');
                wrapper_elem.appendChild(select_button_elem);

                const deselect_button_elem = document.createElement('button');
                deselect_button_elem.classList.add('project-customize-select-button');
                deselect_button_elem.type = 'button';
                deselect_button_elem.innerText = 'Deselect All';
                deselect_button_elem.setAttribute('data-action', 'deselect');
                wrapper_elem.appendChild(deselect_button_elem);
            }
        }

        for(const attribute of attributes) {
            if(category_name === 'custom') {
                input_group_elem.appendChild(this._genCustomAttribute(asset_type, attribute));

            } else {
                const visible_type = breakCamelCase(stripCharsFromEnd(attribute.attribute_type, ('Attribute').length)).trim();
                const option_id = this.form_id+'_'+asset_type+'_'+attribute.internal_name;

                const wrapper_elem = document.createElement('div');
                input_group_elem.appendChild(wrapper_elem);

                const input_elem = document.createElement('input');
                input_elem.classList.add('me-1');
                input_elem.id = option_id;
                input_elem.type = 'checkbox';
                input_elem.setAttribute('data-internalname', attribute.internal_name);
                if(attribute.required) {
                    input_elem.disabled = true;
                    input_elem.checked = true;
                }
                if(attribute.used) { input_elem.checked = true; }
                wrapper_elem.appendChild(input_elem);

                const label_elem = document.createElement('label');
                label_elem.title = visible_type;
                label_elem.htmlFor = option_id;
                label_elem.innerText = attribute.name;
                if(attribute.required) {
                    label_elem.innerHTML = '<span class="red fw-bold me-1">*</span>'+label_elem.innerHTML;
                }
                wrapper_elem.appendChild(label_elem);
            }
        }

        const validation_text_elem = document.createElement('span');
        validation_text_elem.classList.add('validation-text');
        input_group_elem.appendChild(validation_text_elem);

        this.asset_data[asset_type].input_group_elems[category_name] = input_group_elem;
        this.asset_data[asset_type].asset_type_container.appendNode(input_group_elem);

    }

    _addCustomButton(asset_type, datatype_nickname, datatype) {
        const add_custom_attrib_button = document.createElement('button');
        add_custom_attrib_button.type = 'button';
        add_custom_attrib_button.classList.add('add-custom-attribute-button');
        add_custom_attrib_button.innerText = `Add ${datatype_nickname}`;

        add_custom_attrib_button.addEventListener('click', () => {
            const input_group_elem = document.getElementById(this.form_id+'_'+asset_type+'_custom');
            const custom_attribute_id = genId();
            const attribute_type = datatype;
            const new_custom_attribute = {
                'category_name': 'custom',
                'name': 'custom_'+custom_attribute_id+'_Unnamed',
                'internal_name': 'custom_'+custom_attribute_id,
                'attribute_type': attribute_type,
                'used': true,
                'new': true,
            };

            if(([AttributeType.SingleOption, AttributeType.MultiOption, AttributeType.DropdownOption]).includes(attribute_type)) {
                new_custom_attribute.options = ['Option 1', 'Option 2', '...', 'Option 10'];
            }

            this.asset_data[asset_type].attributes.push(new_custom_attribute);
            input_group_elem.appendChild(this._genCustomAttribute(asset_type, new_custom_attribute));
        });

        this.asset_data[asset_type].asset_type_container.appendNode(add_custom_attrib_button);
    }

    _addColorFields(color_fields, asset_type, container) {
        if(color_fields.length === 0) { return; }
        const feature_type = ASSET_TYPE_FEATURE_TYPE[asset_type] ?? null;

        const color_fields_elem = document.createElement('div');
        color_fields_elem.classList.add('input-row');

        const heading_elem = document.createElement('div');
        color_fields_elem.appendChild(heading_elem);

        const label_elem = document.createElement('h5');
        label_elem.classList.add('mb-0');
        label_elem.textContent = 'Map Symbol Display Colors:';
        heading_elem.appendChild(label_elem);

        const sublabel_elem = document.createElement('h6');
        sublabel_elem.classList.add('mb-2', 'fst-italic');
        sublabel_elem.textContent = 'Click color below to modify';
        heading_elem.appendChild(sublabel_elem);

        const spacer_elem = document.createElement('hr');
        spacer_elem.classList.add('color-field-spacer');
        color_fields_elem.appendChild(spacer_elem);

        let last_attribute_name = '';
        for(const color_field of color_fields) {
            const input_id = genId();
            const sep_pos = color_field.indexOf(':');
            const attribute_name = toTitleCase(color_field.slice(0, sep_pos));
            const option_name_raw = color_field.slice(sep_pos+1);
            const option_name = toTitleCase(option_name_raw);
            const asset_color_vals = this.color_field_values || {};
            const color_value = (asset_color_vals[color_field] ?? DEFAULT_COLOR_FIELD_VALUES[color_field] ?? HexColor.Black);
            const symbol_value = asset_color_vals['symbol:'+color_field];

            if(last_attribute_name !== attribute_name) {
                if(last_attribute_name !== '') {
                    const spacer_elem = document.createElement('hr');
                    spacer_elem.classList.add('color-field-spacer');
                    color_fields_elem.appendChild(spacer_elem);
                }
                last_attribute_name = attribute_name;
            }

            const wrapper_elem = document.createElement('div');
            wrapper_elem.classList.add('color-field-wrapper', 'd-flex', 'align-items-center', 'gap-1', 'mb-1');
            wrapper_elem.setAttribute('data-colorfieldname', color_field);
            color_fields_elem.appendChild(wrapper_elem);

            const input_elem = document.createElement('input');
            input_elem.classList.add('color-field-input');
            input_elem.id = input_id;
            input_elem.type = 'color';
            input_elem.value = color_value;
            wrapper_elem.appendChild(input_elem);

            const is_numeric_field = !['minimum', 'maximum'].includes(option_name_raw);
            const repeated_color_field_feature_type = this.repeated_color_fields_feature_type[color_field];
            const is_repeated_color_field = (asset_type===null);

            if(is_numeric_field && (!is_repeated_color_field || repeated_color_field_feature_type!==null)) {
                const color_field_feature_type = (is_repeated_color_field ? repeated_color_field_feature_type : feature_type);
                const picker = new SymbolPicker(symbol_value, this.getMapSymbolSvgs(color_field_feature_type));
                wrapper_elem.appendChild(picker.generateInput());
            }

            const label_elem = document.createElement('span');
            label_elem.classList.add('color-field-label');
            label_elem.textContent = `${attribute_name} - ${option_name}`;
            wrapper_elem.appendChild(label_elem);

        }

        container.appendNode(color_fields_elem);
    }

    _handleSelectAllButtons(container) {
        const buttons = container.body_elem.querySelectorAll('.project-customize-select-button');
        for(const button of buttons) {
            const action = button.dataset.action;
            const checkboxes = button.parentNode.parentNode.parentNode.querySelectorAll('input[type="checkbox"]');

            button.addEventListener('click', () => {
                for(const checkbox of checkboxes) {
                    if(checkbox.disabled) { continue; }
                    const new_checked_state = (action==='select');
                    if(checkbox.checked !== new_checked_state) {
                        checkbox.checked = new_checked_state;
                        checkbox.dispatchEvent(new Event('change'));
                    }
                }
            });
        }
    }

    generateForm() {
        for(const asset_type of this.asset_types) {
            const container = new CollapsingDiv(toTitleCase(asset_type), this.multiple_asset_types);
            this.asset_data[asset_type].asset_type_container = container;

            for(const category_name in this.asset_data[asset_type].attributes_by_category) {
                this._addAttributeCategory(asset_type, category_name, this.asset_data[asset_type].attributes_by_category[category_name]);
            }

            if(!this.asset_data[asset_type].does_have_custom_attribs) {
                this._addAttributeCategory(asset_type, 'custom', []);
            }

            for(const datatype_nickname in ProjectCustomizeForm.DATATYPE_NICKNAMES) {
                this._addCustomButton(asset_type, datatype_nickname, ProjectCustomizeForm.DATATYPE_NICKNAMES[datatype_nickname]);
            }

            this._handleSelectAllButtons(container);

            if(this.asset_data[asset_type].color_fields.length > 0) {
                container.appendNode(document.createElement('hr'));
                this._addColorFields(this.asset_data[asset_type].color_fields, asset_type, container);
            }

            container.appendNode(document.createElement('hr'));

            this.form_elem.appendChild(container.wrapper_elem);
        }

        if(this.repeated_color_fields.size > 0) {
            const container = new CollapsingDiv(`Shared Map Colors`, this.multiple_asset_types);
            this._addColorFields([...this.repeated_color_fields], null, container);
            this.form_elem.appendChild(container.wrapper_elem);
            this.form_elem.appendChild(document.createElement('hr'));
            this.form_elem.appendChild(document.createElement('br'));
            this.shared_color_fields_container = container.wrapper_elem;
        }
    }

    validateForm() {
        const used_attributes = {};
        const custom_attributes = {};
        const color_field_values = {};
        let did_have_validation_errors = false;
        let did_scroll_to_error = false;

        const saveColorFields = ((container) => {
            for(const color_field_wrapper of container.querySelectorAll('.color-field-wrapper')) {
                const color_field_name = color_field_wrapper.dataset.colorfieldname;

                const color_input = color_field_wrapper.querySelector('.color-field-input');
                color_field_values[color_field_name] = color_input.value;

                const symbol_input = color_field_wrapper.querySelector('.symbol-picker-container');
                if(!symbol_input) { continue; }
                color_field_values['symbol:'+color_field_name] = symbol_input.getAttribute('data-symbol-key');
            }
        });

        for(const asset_type of this.asset_types) {
            used_attributes[asset_type] = [];
            custom_attributes[asset_type] = [];

            for(const attribute of this.asset_data[asset_type].attributes) {
                const category_name = attribute.category_name;
                const category_elem = this.asset_data[asset_type].input_group_elems[category_name];
                const input_elem = category_elem.querySelector(`#${this.form_id}_${asset_type}_${attribute.internal_name}`);

                if(category_name === 'custom') {
                    let did_have_validation_error = false;
                    const attribute_type = attribute.attribute_type;
                    const using_elem = input_elem.querySelector('.custom-attribute-using-checkbox');
                    if(using_elem.checked) { used_attributes[asset_type].push(attribute.internal_name); }

                    const name_elem = input_elem.querySelector('.custom-attribute-name input');
                    if(isBlankValue(name_elem.value)) {
                        name_elem.classList.add('bad-input');
                        did_have_validation_error = true;
                        if(!did_scroll_to_error) {
                            name_elem.scrollIntoView();
                            did_scroll_to_error = true;
                        }
                        alert('Must specify name for attribute.');

                    } else if(name_elem.value.length > CUSTOM_ATTRIB_NAME_MAX_LEN) {
                        name_elem.classList.add('bad-input');
                        did_have_validation_error = true;
                        if(!did_scroll_to_error) {
                            name_elem.scrollIntoView();
                            did_scroll_to_error = true;
                        }
                        alert('Attribute name is too long (max '+CUSTOM_ATTRIB_NAME_MAX_LEN+' chars)');

                    } else {
                        name_elem.classList.remove('bad-input');
                    }

                    const new_custom_attribute = {
                        'category_name': 'custom',
                        'name': attribute.internal_name+'_'+name_elem.value,
                        'internal_name': attribute.internal_name,
                        'attribute_type': attribute_type,
                    };

                    if(([AttributeType.SingleOption, AttributeType.MultiOption, AttributeType.DropdownOption]).includes(attribute_type)) {
                        const options_elem = input_elem.querySelector('.custom-attribute-value textarea');

                        if(options_elem.value.length > CUSTOM_ATTRIB_VALUE_MAX_LEN) {
                            options_elem.classList.add('bad-input');
                            did_have_validation_error = true;
                            if(!did_scroll_to_error) {
                                options_elem.scrollIntoView();
                                did_scroll_to_error = true;
                            }
                            alert('Attribute options are too long (max '+CUSTOM_ATTRIB_VALUE_MAX_LEN+' chars total)');

                        } else {
                            new_custom_attribute.options = options_elem.value.split(/\r?\n/).filter((value) => !isBlankValue(value));

                            if(new_custom_attribute.options.length === 0) {
                                options_elem.classList.add('bad-input');
                                did_have_validation_error = true;
                                if(!did_scroll_to_error) {
                                    options_elem.scrollIntoView();
                                    did_scroll_to_error = true;
                                }
                                alert('Must specify options for '+attribute_type);

                            } else {
                                options_elem.classList.remove('bad-input');
                            }
                        }

                    } else if(attribute_type === AttributeType.String) {
                        new_custom_attribute.minLen = 0;
                        new_custom_attribute.maxLen = 250;

                    } else if(attribute_type === AttributeType.BigString) {
                        new_custom_attribute.minLen = 0;
                        new_custom_attribute.maxLen = 500;
                        new_custom_attribute.unit = 'Max 500 chars';


                    } else if(([AttributeType.Number, AttributeType.Integer]).includes(attribute_type)) {
                        new_custom_attribute.minVal = -100000;
                        new_custom_attribute.maxVal = 100000;

                    } else {
                        console.error('Unhandled attribute `'+attribute_type+'`');

                    }

                    if(did_have_validation_error) {
                        did_have_validation_errors = true;
                        continue;
                    }
                    delete new_custom_attribute['new'];
                    custom_attributes[asset_type].push(new_custom_attribute);

                } else {
                    if(input_elem.checked) { used_attributes[asset_type].push(attribute.internal_name); }

                }
            }

            saveColorFields(this.asset_data[asset_type].asset_type_container.body_elem);

            used_attributes[asset_type].sort();
        }

        if(this.shared_color_fields_container) { saveColorFields(this.shared_color_fields_container); }

        if(did_have_validation_errors) { return false; }
        return {
            'used_attribute_internal_names': used_attributes,
            'custom_attributes': custom_attributes,
            'color_field_values': color_field_values,
        };
    }
}